<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import helperService from "@/services/helper";
import {
  getMedicalInsuranceName,
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
} from "@/services/patient-utils";
import PatientList from "@/components/patient/PatientList.vue";
import AppointmentSummary from "@/components/appointment/AppointmentSummary.vue";
import IndicationForm from "@/components/appointment/indications/IndicationForm.vue";
import patientService from "@/services/patient";
import pagination from "@/mixins/pagination";
import { get } from "lodash";
import { isIndicationsEnabled } from "@/services/config";
import { cloneDeep, debounce } from "lodash";
import { INDICATIONS_TYPES } from "@/services/constants";
import indicationService from "@/services/indication";
import { issuedWithoutEngineNotice } from "@/services/indication-fallback-notice";
import { filterPersistibleIndications } from "@/services/indication-filter";
import formService from "@/services/form";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "Prescription",
  mixins: [pagination],
  components: {
    BaseSubNav,
    PatientList,
    AppointmentSummary,
    IndicationForm,
  },
  data() {
    return {
      searchInput: "",
      patients: [],
      patientSelected: null,
      isSavingIndications: false,
      savedIndications: [],
      completedForms: [],
      isIndicationsEnabled,
      appointmentStatus: "appointment",
      patientIndicationsFilter: ["medications", "images", "lab", "practices"],
      forms: cloneDeep(INDICATIONS_TYPES),
      prescriptionForms: [],
      showPatientTable: true,
    };
  },
  created() {
    this.prescriptionForms = this.forms.filter((form) =>
      ["medications", "images", "lab", "practices"].includes(form.type)
    );

    // UC-REG-123: removido el auto-select por `localStorage.newlyCreatedPatient`.
    // El diseño original auto-seleccionaba el paciente recién creado al
    // entrar a /prescription, pero quedaba persistido y aparecía como
    // default aunque el usuario hubiera navegado a otra parte y volviera
    // más tarde (caso reportado Federico, demo CNC 2026-06-16). Ahora
    // /prescription siempre arranca sin paciente — el médico tiene que
    // buscarlo desde el form. Limpiamos el localStorage por las dudas
    // (basura de versiones anteriores que setteaban el key).
    localStorage.removeItem("newlyCreatedPatient");

    this.$bus.$on("updateIndication", (indication, isDeleted = false) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indication.key
      );
      if (indicationIndex !== -1) {
        if (!isDeleted) {
          this.$set(this.savedIndications, indicationIndex, indication);
        } else {
          this.savedIndications.splice(indicationIndex, 1);
        }
      } else {
        if (!isDeleted) {
          this.savedIndications.push(indication);
        }
      }
    });

    this.$bus.$on("filesUploaded", (indicationKey) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indicationKey
      );
      if (indicationIndex !== -1) {
        this.$set(this.savedIndications, indicationIndex, {
          ...this.savedIndications[indicationIndex],
          hasFiles: false,
        });
      }

      if (this.savedIndications.every((indication) => !indication.hasFiles)) {
        this.isSavingIndications = false;
        this.$router.push({ name: "home" });
      }
    });

    this.$bus.$on("form-completed", this.updateCompletedForms);
    this.$bus.$on("form-updated", this.updateCompletedForms);
  },

  beforeDestroy() {
    this.$bus.$off("form-completed", this.updateCompletedForms);
    this.$bus.$off("form-updated", this.updateCompletedForms);
  },

  methods: {
    updateCompletedForms() {
      const indicationFormRef = this.$refs.indicationForm;
      if (!indicationFormRef) return;

      const allForms = [];
      const allIndicationRefs = indicationFormRef.$refs;

      for (const refName of Object.keys(allIndicationRefs)) {
        if (refName.startsWith("indications-")) {
          const indicationComponent = allIndicationRefs[refName];
          if (indicationComponent?.[0]?.completedForms) {
            const completedForms = indicationComponent[0].completedForms;
            for (const indicationKey of Object.keys(completedForms)) {
              allForms.push(...completedForms[indicationKey]);
            }
          }
        }
      }
      this.completedForms = allForms;
    },
    searchPatient() {
      if (!this.searchInput.trim()) {
        this.patients = [];
        this.pagination.total = 0;
        return;
      }

      this.showPatientTable = true;

      const query = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
      };

      const input = this.searchInput && this.searchInput.split(" ");
      const useSearchIndex = input && (input.length === 1 || input.length > 3);

      const useNameIndex = input && input.length > 1 && input.length <= 3;

      if (useSearchIndex) {
        query.$text = {
          $search: this.searchInput,
        };
      } else if (useNameIndex) {
        Object.assign(query, helperService.createNameQuery(this.searchInput));
      }

      this.getPatients(query);
    },
    debounceInput: debounce(function () {
      this.searchPatient();
    }, DEBOUNCE_MS_TIMEOUT),
    getFullName(patient) {
      return helperService.getFullName(patient);
    },
    getMedicalInsuranceName,
    getMedicalInsurancePlan,
    getMedicalInsuranceNumber,
    get,
    getPatients(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      defaultQuery.doctors = this.doctorFilter || undefined;

      patientService
        .get(defaultQuery)
        .then((res) => {
          this.patients = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    selectPatient(patientId) {
      this.patientSelected = this.patients.find(
        (patient) => patient._id === patientId
      );
      this.showPatientTable = false;
      this.searchInput = "";
    },
    canSaveIndications() {
      const hasIndications =
        this.savedIndications.length > 0 &&
        this.savedIndications.some(
          (indication) =>
            !indication.createdAt &&
            this.patientIndicationsFilter.includes(indication.type)
        );

      const hasCompletedForms = this.completedForms.length > 0;

      return !this.isSavingIndications && (hasIndications || hasCompletedForms);
    },

    getAllCompletedForms() {
      return this.completedForms;
    },
    triggerUpload(createdIndication) {
      this.$refs.indicationForm.triggerUpload(createdIndication);
    },
    async saveIndications(typeFilter = null) {
      try {
        const validIndications = filterPersistibleIndications(
          this.savedIndications,
          typeFilter
        );

        // Sin esta guarda, si no hay indicaciones válidas ni formularios el
        // service no posteaba nada pero igual se mostraba "Prescripciones
        // enviadas correctamente" y se navegaba al home (bug BUG-001).
        const hasForms = this.completedForms && this.completedForms.length > 0;
        if (!validIndications.length && !hasForms) {
          this.$message.warning(
            "Agregá al menos un medicamento u orden antes de enviar"
          );
          return;
        }

        const createdIndications = await indicationService.saveIndications(
          validIndications,
          this.patientSelected._id,
          undefined, // No appointmentId en Prescription
          typeFilter,
          this.$store,
          {
            isSavingIndications: this.isSavingIndications,
            onTriggerUpload: this.triggerUpload.bind(this),
            onUpdateIndication: (updatedIndication) => {
              const index = this.savedIndications.findIndex(
                (ind) => ind.key === updatedIndication.key
              );
              if (index !== -1) {
                this.$set(this.savedIndications, index, updatedIndication);
              }
            },
            onSetSavingStatus: (status) => {
              this.isSavingIndications = status;
            },
          }
        );

        await this.sendCompletedForms();

        const hasPendingFiles = this.savedIndications.some(
          (indication) => indication.hasFiles
        );

        if (!hasPendingFiles) {
          this.$message.success("Prescripciones enviadas correctamente");
          // UC-REG-259: si la API emitió alguna receta sin el motor externo
          // (fallback local por tenant, Campana), aviso suave además del
          // éxito. Sin recetas marcadas no muestra nada.
          const notice = issuedWithoutEngineNotice(createdIndications);
          if (notice) {
            this.$message({
              type: "warning",
              message: notice,
              duration: 10000,
              showClose: true,
            });
          }
          this.$router.push({ name: "home" });
        }
      } catch (error) {
        console.log(error);
      }
    },

    async sendCompletedForms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];
            if (indicationComponent?.[0]?.completedForms) {
              const completedForms = indicationComponent[0].completedForms;

              for (const indicationKey of Object.keys(completedForms)) {
                const formsToSend = completedForms[indicationKey];

                for (const completedForm of formsToSend) {
                  if (!completedForm.isSent) {
                    await this.sendFormToBackend(
                      completedForm,
                      indicationKey,
                      indicationComponent[0]
                    );
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error al enviar formularios completados:", error);
      }
    },

    async sendFormToBackend(completedForm, indicationKey, indicationComponent) {
      try {
        const formData = {
          form: completedForm.form?._id || completedForm._id,
          patient: this.patientSelected._id,
          sections: completedForm.sections || [],
          answeredAt: completedForm.answeredAt || new Date(),
          status: completedForm.status || "open",
          createdBy: this.$store.state.user,
        };

        const createdForm = await formService.postPatientForm(formData);

        completedForm.isSent = true;
        completedForm._id = createdForm._id;
        completedForm.sentAt = new Date();

        if (completedForm.hasFiles && completedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(
            createdForm._id,
            completedForm.dropzoneFiles
          );
        }

        this.updateIndication(indicationComponent);
      } catch (error) {
        console.error("Error al enviar formulario al backend:", error);
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    updateIndication(indication) {
      this.$bus.$emit("updateIndication", cloneDeep(indication), false);
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav
    template(#actions)
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        placeholder="Buscar"
        :clearable="true"
        @input="debounceInput"
      )
      button.button.button--blue.is-compact(@click="searchPatient")
        micon(name="search").button__icon
        span.button__text Buscar
      router-link.button.button--blue.is-compact(
        v-auth="'patient.create'"
        :to="{name: 'create-patient'}"
      )
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Paciente
  .box.box--with-subnav(v-show="showPatientTable")
    .box__content--stretch(style="max-height: 400px; overflow-y: auto;")
      table
        thead
          tr
            th Paciente
            th Seguro Salud
            th Plan
            th Nro Afiliado
            th Documento
            th Edad
            th Última Atención
        tbody
          tr(v-if="patients.length === 0")
            td(colspan="7" style="text-align: center; padding: 40px; color: #909399;")
              | No hay resultados
          tr.clickable(v-else v-for="patient in patients" :key="patient._id" @click="selectPatient(patient._id)")
            td {{ getFullName(patient) }}
            td {{ getMedicalInsuranceName(patient) }}
            td {{ getMedicalInsurancePlan(patient) }}
            td {{ getMedicalInsuranceNumber(patient) }}
            td {{ patient.governmentId?.number || 'N/A' }}
            td {{ patient.bornAt | age }}
            td {{ patient.patient?.lastEvent?.createdAt | formatDate }}
  div.prescription-container(v-show="patientSelected")
    .whiteBox
      .patient-header-container.top
        .patient-header-top
          p.secondary-text Prescribiendo a
          h1 {{ getFullName(patientSelected) }}
        .patient-header-top
          button.button-blue-new.is-compact(
            @click="saveIndications(patientIndicationsFilter)"
            :disabled="!canSaveIndications() || isSavingIndications"
          ) Enviar prescripciones
      .patient-header-container
        .patient-header-bottom
          p.secondary-text Edad
          p.primary-text {{ patientSelected?.bornAt | age }}
        .patient-header-bottom
          p.secondary-text DNI
          p.primary-text {{ get(patientSelected, 'governmentId.number', 'N/A') }}
        .patient-header-bottom
          p.secondary-text Teléfono
          p.primary-text {{ patientSelected?.phone }}
        .patient-header-bottom
          p.secondary-text E-mail
          p.primary-text {{ patientSelected?.email }}
        .patient-header-bottom
          p.secondary-text Seguro Salud
          p.primary-text {{ getMedicalInsuranceName(patientSelected) }}
        .patient-header-bottom
          p.secondary-text Cobertura
          p.primary-text {{ getMedicalInsurancePlan(patientSelected) }}
        .patient-header-bottom
          p.secondary-text # de socio
          p.primary-text {{ getMedicalInsuranceNumber(patientSelected) }}
    .patient-attention-container
      .patient-attention-left
        indication-form(
          v-show="isIndicationsEnabled && appointmentStatus === 'appointment'"
          ref="indicationForm"  
          :forms="prescriptionForms"
          :savedIndications="savedIndications"
        )

      .patient-attention-right
          AppointmentSummary( 
            :savedIndications="savedIndications" 
            :context="'prescription'"
            :completedForms="getAllCompletedForms"
          )
</template>

<style lang="scss">
.prescription-container {
  margin-top: 20px;
}

.patient-attention-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: row;

  .patient-attention-right {
    width: 500px;
    margin-left: 20px;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
    min-height: 500px;
    overflow: hidden;
  }

  .patient-attention-left {
    width: 100%;
    padding: 15px 25px;
    margin-bottom: 20px;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
    min-height: 600px;
  }
}

.clickable {
  cursor: pointer;
}

.whiteBox {
  width: 100%;
  padding: 15px 25px;
  margin-bottom: 20px;
  background-color: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);

  .secondary-text {
    font-size: 13px;
    margin: 2px 0;
  }

  .primary-text {
    color: $primary;
    font-weight: 600;
    margin: 2px 0;
  }

  .patient-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    .patient-header-bottom {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }
  }

  .patient-header-container.top {
    padding-bottom: 8px;
    margin-bottom: 12px;
    border-bottom: solid 1px #00000024;

    .patient-header-top {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;

      h1 {
        margin: 0;
        font-size: 20px;
      }
    }
  }
}
</style>
